import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {MenuItem} from '@components';
import {PfImage} from '@profabric/react-components';
import styled from 'styled-components';
import {SidebarSearch} from '@app/components/sidebar-search/SidebarSearch';
import i18n from '@app/utils/i18n';

export interface IMenuItem {
  name: string;
  path?: string;
  children?: Array<IMenuItem>;
}

export const MENU: IMenuItem[] = [
  {
    name: i18n.t('menusidebar.label.dashboard'),
    path: '/'
  },
  {
    name: "Todo's",
    children: [
      {name: 'My Todos', path: '/todos'},
      {name: 'Archived Todos', path: '/archives'}
    ]
  },
  {
    name: 'Dailies',
    path: '/my-dailies'
  },
  {
    name: 'All Dailies',
    path: '/all-dailies'
  },
  {
    name: 'All Leaves',
    path: '/all-leaves'
  },
  {
    name: 'All Users',
    path: '/all-users'
  },
  {
    name: 'Users',
    children: [
      {
        name: 'Create User',
        path: '/create-user'
      },
      {
        name: 'All Users',
        path: '/all-users'
      }
    ]
  },
  {
    name: 'Leaves',
    children: [
      {
        name: 'Apply',
        path: '/apply-leave'
      },
      {
        name: 'My Leaves',
        path: '/my-leaves'
      },
      {
        name: 'Leave Policy',
        path: '/leave-policy'
      }
    ]
  },
  {
    name: 'Clients',
    children: [
      {
        name: 'Create Client',
        path: '/create-client'
      },
      {
        name: 'All Clients',
        path: '/all-clients'
      }
    ]
  },
  {
    name: 'Cases',
    children: [
      {
        name: 'Create Cases',
        path: '/create-case'
      },
      {
        name: 'All Cases',
        path: '/all-cases'
      }
    ]
  },
  {
    name: 'All Projects',
    path: '/projects'
  },
  {
    name: 'Projects',
    children: [
      {
        name: 'Create Project',
        path: '/create-project'
      },
      {
        name: 'All Projects',
        path: '/projects'
      }
    ]
  },
  {
    name: "Today's Case",
    path: '/cases'
  },
  {
    name: 'Tasks',
    path: '/tasks'
  }
];

const getIngoredMenus = (ignoreMenus) => {
  const updatedSideBar = MENU.filter(
    (menu) => !ignoreMenus.includes(menu.name)
  );

  return updatedSideBar;
};

//log
// console.log(getIngoredMenus(['Leaves', 'Dailies', 'All Projects']), "admin")

const getSideBarMenu = (user) => {
  if (!user) {
    return;
  }
  const [userType] = user;

  if (userType === 'admin') {
    const ignoreMenus = ['Leaves', 'Dailies', 'All Projects'];

    return getIngoredMenus(ignoreMenus);
  }

  if (userType === 'superAdmin') {
    const ignoreMenus = ['Leaves', 'Dailies', 'All Projects'];

    return getIngoredMenus(ignoreMenus);
  }

  if (userType === 'hr') {
    const ignoreMenus = [
      'Clients',
      'Documentation',
      'Cases',
      'All Users',
      'Cases',
      'Projects',
      "Today's Case",
      'Tasks',
      "Todo's",
      'All Projects'
    ];

    return getIngoredMenus(ignoreMenus);
  }

  if (userType === 'sales') {
    const ignoreMenus = [
      'Clients',
      'Users',
      'Documentation',
      'All Dailies',
      'All Leaves',
      'Projects',
      'All Users',
      'All Projects'
    ];

    return getIngoredMenus(ignoreMenus);
  }

  const ignoreMenus = [
    'Clients',
    'Users',
    'Documentation',
    'Cases',
    'All Dailies',
    'Cases',
    'All Leaves',
    'Projects',
    "Today's Case",
    'All Users'
  ];

  // console.log(getIngoredMenus(ignoreMenus), ' getIngoredMenus(ignoreMenus)');
  return getIngoredMenus(ignoreMenus);
};

const StyledBrandImage = styled(PfImage)`
  float: left;
  line-height: 0.8;
  margin: -1px 8px 0 6px;
  opacity: 0.8;
  --pf-box-shadow: 0 10px 20px rgba(0, 0, 0, 0.19),
    0 6px 6px rgba(0, 0, 0, 0.23) !important;
`;

const StyledUserImage = styled(PfImage)`
  --pf-box-shadow: 0 3px 6px #00000029, 0 3px 6px #0000003b !important;
`;

const MenuSidebar = () => {
  const user = useSelector((state: any) => state.auth.currentUser);
  const sidebarSkin = useSelector((state: any) => state.ui.sidebarSkin);
  const menuItemFlat = useSelector((state: any) => state.ui.menuItemFlat);
  const menuChildIndent = useSelector((state: any) => state.ui.menuChildIndent);
  return (
    <aside className={`main-sidebar elevation-4 ${sidebarSkin}`}>
      <Link to="/" className="brand-link">
        <StyledBrandImage
          src="/img/logo.png"
          alt="ArtzenWiseLogo"
          width={33}
          height={33}
          rounded
        />
        <span className="brand-text font-weight-light">Wise</span>
      </Link>
      <div className="sidebar">
        <div className="user-panel mt-3 pb-3 mb-3 d-flex">
          <div className="image">
            <StyledUserImage
              src={user?.imageUrl}
              fallbackSrc="/img/default-profile.png"
              alt="User"
              width={34}
              height={34}
              rounded
            />
          </div>
          <div className="info">
            <h5 className="text-light">{user?.data?.name}</h5>
            <Link to="/profile" className="d-block">
              {user?.data?.email}
            </Link>
          </div>
        </div>

        <div className="form-inline">
          <SidebarSearch />
        </div>

        <nav className="mt-2" style={{overflowY: 'hidden'}}>
          <ul
            className={`nav nav-pills nav-sidebar flex-column${
              menuItemFlat ? ' nav-flat' : ''
            }${menuChildIndent ? ' nav-child-indent' : ''}`}
            role="menu"
          >
            {getSideBarMenu(user?.data?.permissions).map(
              (menuItem: IMenuItem, index) => (
                <MenuItem key={index} menuItem={menuItem} />
              )
            )}
          </ul>
        </nav>
      </div>
    </aside>
  );
};

export default memo(MenuSidebar);
